import React, { useState, useEffect, useCallback } from 'react';
import { ThemeTokenEditor, type ThemeTokens, themesApi } from '..';
import { editorApi, type SiteRecord } from '../../editor';
import { Save, Check, Globe } from 'lucide-react';

const webbuilderApi = { ...editorApi, ...themesApi };

const initialTokens: ThemeTokens = {
  primary: '222 47% 24%',
  background: '0 0% 100%',
  foreground: '222 47% 11%',
  accent: '354 71% 48%',
  headingFont: 'Playfair Display',
  bodyFont: 'Inter',
  darkMode: false,
};

// ThemesPage composes the Brand Theme Designer and token settings (R-MOD-2).
export function ThemesPage(): React.JSX.Element {
  const [tokens, setTokens] = useState<ThemeTokens>(initialTokens);
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [activeSiteId, setActiveSiteId] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Load sites
  useEffect(() => {
    (async () => {
      try {
        const data = await webbuilderApi.listSites();
        if (Array.isArray(data) && data.length > 0) {
          setSites(data);
          setActiveSiteId(data[0].id);
        }
      } catch {
        // Fallback
      }
    })();
  }, []);

  // Load active theme for selected site
  const loadTheme = useCallback(async (siteId: string) => {
    if (!siteId) return;
    try {
      const theme = await webbuilderApi.getActiveTheme(siteId);
      if (theme) {
        let parsedTokens: Record<string, string> = {};
        if (typeof theme.tokens_json === 'string') {
          try {
            parsedTokens = JSON.parse(theme.tokens_json);
          } catch {
            parsedTokens = {};
          }
        } else if (theme.tokens_json) {
          parsedTokens = theme.tokens_json as Record<string, string>;
        }

        setTokens({
          primary: parsedTokens.primary || initialTokens.primary,
          background: parsedTokens.background || initialTokens.background,
          foreground: parsedTokens.foreground || initialTokens.foreground,
          accent: parsedTokens.accent || initialTokens.accent,
          headingFont: theme.heading_font || initialTokens.headingFont,
          bodyFont: theme.body_font || initialTokens.bodyFont,
          darkMode: Boolean(theme.dark_mode_enabled),
        });
      }
    } catch {
      // Keep current tokens
    }
  }, []);

  useEffect(() => {
    if (activeSiteId) {
      loadTheme(activeSiteId);
    }
  }, [activeSiteId, loadTheme]);

  const handleSave = async () => {
    if (!activeSiteId) return;
    setIsSaving(true);
    try {
      await webbuilderApi.updateActiveTheme(activeSiteId, {
        tokens_json: {
          primary: tokens.primary,
          background: tokens.background,
          foreground: tokens.foreground,
          accent: tokens.accent,
        },
        heading_font: tokens.headingFont,
        body_font: tokens.bodyFont,
        dark_mode_enabled: tokens.darkMode,
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to save theme', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      {/* Site Selector and Save Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-card border border-border p-4 rounded-xl">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Select Site:</span>
          <select
            value={activeSiteId}
            onChange={(e) => setActiveSiteId(e.target.value)}
            className="text-sm font-medium bg-background border border-border rounded-md px-3 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary min-h-[38px]"
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.subdomain_slug})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <Check className="h-4 w-4" /> Theme Saved
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || !activeSiteId}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50 min-h-[44px]"
          >
            <Save className="h-4 w-4" />
            <span>{isSaving ? 'Saving...' : 'Save Theme Tokens'}</span>
          </button>
        </div>
      </div>

      <ThemeTokenEditor
        tokens={tokens}
        onChange={setTokens}
        onApplyPreset={(p) => {
          if (p === 'school') {
            setTokens(initialTokens);
          } else if (p === 'app') {
            setTokens({ ...initialTokens, primary: '210 100% 50%', accent: '142 71% 45%' });
          } else {
            // Coach vertical: bold indigo / warm amber
            setTokens({ ...initialTokens, primary: '262 83% 58%', accent: '38 92% 50%', headingFont: 'Inter' });
          }
        }}
      />
    </div>
  );
}

export const handle = { crumb: 'Themes & Brand System' };
